import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Data & Engineering',
    items: [
      { name: 'Data Scientist', icon: '📊' },
      { name: 'Data Engineer (AI Pipelines)', icon: '🔗' },
      { name: 'MLOps Engineer', icon: '🔧' },
      { name: 'Responsible AI Specialist', icon: '🛡️' },
    ]
  },
  {
    title: 'Research & Modeling',
    items: [
      { name: 'AI Research Scientist', icon: '🔬' },
      { name: 'Deep Learning Engineer', icon: '🧠' },
      { name: 'NLP Engineer', icon: '💬' },
      { name: 'Computer Vision Engineer', icon: '👁️' },
    ]
  },
  {
    title: 'Product & Deployment',
    items: [
      { name: 'Generative AI Developer', icon: '🤖' },
      { name: 'LLM/Prompt Engineer', icon: '✍️' },
      { name: 'ML Engineer', icon: '⚙️' },
      { name: 'AI Product Manager', icon: '📋' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Deep Technical AI/ML Expertise',
    text: 'Our recruiters are trained in AI/ML domains — they understand PyTorch vs TensorFlow, transformer architectures, feature engineering, and MLOps toolchains. Every candidate assessment goes beyond buzzwords.',
  },
  {
    icon: '✔',
    title: 'Rigorous Technical Vetting',
    text: 'Candidates are assessed for: hands-on ML model development & deployment, proficiency in Python, PyTorch, TensorFlow, Scikit-learn, cloud AI platforms (AWS SageMaker, Azure ML, GCP Vertex AI), and domain-specific expertise.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, contract-to-hire, or full-time placements. From a single data scientist to an entire AI center of excellence — we scale our model to fit your project scope and budget.',
  },
  {
    icon: '🛡',
    title: 'Responsible AI Awareness',
    text: 'Our consultants are screened for awareness of ethical AI, model governance, bias mitigation, and regulatory compliance — critical requirements for enterprise AI deployments.',
  },
];

const problemItems = [
  'Prolonged model development cycles due to engineers unfamiliar with production ML workflows.',
  'Stalled AI roadmaps as teams struggle to find specialists in LLMs, computer vision, or NLP.',
  'Wasted budget on data scientists who lack domain expertise or MLOps skills.',
  'Missed competitive advantage while rivals deploy AI solutions at speed.',
  'Security & compliance risks from AI practitioners inexperienced with responsible AI frameworks.',
];

const solutionPoints = [
  'Specialized AI/ML recruiters who understand your tech stack',
  'Founders and entrepreneurs intimately familiar with the nuances of AI/ML',
  'Data scientists and engineers with a passion for AI/ML',
];

const outcomes = [
  {
    title: 'Faster AI Product Launches',
    text: 'Get qualified AI engineers in days, not months — and ship intelligent features on schedule.',
  },
  {
    title: 'Higher Model Quality',
    text: 'Consultants with real production ML experience reduce model failure rates and rework.',
  },
  {
    title: 'Reduced AI Project Risk',
    text: 'Vetted expertise in responsible AI and MLOps frameworks minimizes technical and regulatory exposure.',
  },
  {
    title: 'Scalable AI Talent Pipeline',
    text: 'Access a ready bench of pre-screened AI/ML professionals aligned to your innovation roadmap.',
  },
  {
    title: 'Accelerated Digital Transformation',
    text: 'Eliminate talent bottlenecks that slow your AI-driven competitive advantage.',
  },
];

const AIMLStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>AI & ML Staffing India | Data Science Recruitment | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides certified AI & ML staffing solutions. Hire pre-vetted data scientists, machine learning engineers, NLP specialists, and MLOps architects for your projects."
        />
        <meta
          name="keywords"
          content="AI staffing India, ML recruitment India, data science staffing, machine learning engineers India, AI consultants India, data scientist recruitment, artificial intelligence staffing, ML developers India, AI talent acquisition, data science consulting India, ML project staffing, AI experts India, data engineer recruitment, AI solution providers, Kapil Technologies AI"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="AI & ML Staffing India | Data Science Recruitment | Kapil Technologies"
        />
        <meta
          property="og:description"
          content="Kapil Technologies provides certified AI & ML staffing solutions. Hire pre-vetted data scientists and machine learning engineers."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI & ML Staffing India | Data Science Recruitment"
        />
        <meta
          name="twitter:description"
          content="Certified AI & ML staffing solutions. Hire pre-vetted data scientists and machine learning engineers."
        />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/ai-ml"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602626/Kapil_Tech_AI_ML_Staffing_o9buph.mp4"
        titleWhite="Certified AI & Machine Learning Talent."
        titleGradient="Delivered Fast. Built for Scale."
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Can't Find the Right AI/ML Engineers for Your Projects?"
        description="Kapil Tech connects enterprises with pre-vetted AI/ML professionals — from data scientists and ML engineers to NLP specialists and MLOps architects — across every major industry and platform. Stop sifting through unqualified resumes. Start building intelligent systems."
        stats={[
          { value: "300+", label: "AI/ML Experts Placed" },
          { value: "95%", label: "Client Retention Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_AI_ML_Staffing_2_avif.heif"
        imageAlt="AI and ML Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="AI/ML Roles & Specializations We Staff"
        description="Our AI/ML talent network spans every role in the modern intelligent systems stack."
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Enterprises Choose Us */}
      <TechStaffingServices 
        title="Why Enterprises Choose Us for AI/ML Staffing"
        description="We combine deep AI domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is the AI Talent Shortage Stalling Your Innovation?"
        description="The global AI talent market is fiercely competitive. Generalist recruiters lack the technical depth to distinguish a machine learning engineer from a data analyst — leading to costly mis-hires, delayed AI initiatives, and failed digital transformation projects."
        problemTitle="The Real Cost of a Wrong AI/ML Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech Solution"
        solutionDescription="Kapil Tech operates exclusively within the technology talent ecosystem, with a focused AI/ML practice staffed by recruiters who understand the difference between a generative AI engineer, a deep learning researcher, and a production ML engineer."
        solutionPoints={solutionPoints}
        ctaText="Find Your AI Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your AI initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="AI/ML Experts?"
        description="Share your AI/ML staffing requirements today. Our team responds within hours with pre-vetted candidates matched to your stack and goals."
      />
    </div>
  );
};

export default AIMLStaffHome;
